import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookHistoryEvent } from '../objects/Book';
import { getAllHistory } from '../repository/readingRepository';

const formatDate = (isoDate: string): string => {
  const date = new Date(isoDate);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/New_York',
  });
};

interface EventDescription {
  prefix: string;
  suffix: string;
}

const getEventDescription = (event: BookHistoryEvent): EventDescription => {
  switch (event.event_type) {
    case 'book_added':
      return {
        prefix: 'Added ',
        suffix: ` with status "${event.details.initial_status}"`,
      };
    case 'status_changed':
      return {
        prefix: '',
        suffix: `: status changed from "${event.details.old_status}" to "${event.details.new_status}"`,
      };
    case 'note_created':
      return {
        prefix: 'Created notes for ',
        suffix: '',
      };
    case 'note_updated':
      return {
        prefix: 'Updated notes for ',
        suffix: '',
      };
    default:
      return { prefix: 'Unknown event for ', suffix: '' };
  }
};

const HistoryPage: React.FC = () => {
  const [history, setHistory] = useState<BookHistoryEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    const fetchHistory = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getAllHistory(100);
        if (isMounted) {
          setHistory(data);
        }
      } catch (err) {
        if (isMounted) {
          setError('Failed to load history');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    fetchHistory();
    return () => {
      isMounted = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="card">
        <div className="card-section">Loading history...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <div className="card-section">
          <div className="alert error">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-section">
        <h2>Activity History</h2>
        <p className="helper-text" style={{ marginBottom: '1rem' }}>
          All your reading activity, most recent first.
        </p>
        {history.length === 0 ? (
          <div className="helper-text">No history recorded yet.</div>
        ) : (
          <div className="history-list">
            {history.map((event) => {
              const title = event.details.title || 'Unknown book';
              const { prefix, suffix } = getEventDescription(event);
              return (
                <div key={event.uuid} className="history-item">
                  <div className="history-date">{formatDate(event.created_at)}</div>
                  <div className="history-description">
                    {prefix}
                    <Link to={`/${event.book_uuid}`} className="history-book-link">
                      {title}
                    </Link>
                    {suffix}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default HistoryPage;
